import React, { useState, useEffect } from "react";
import { Container, Row, Col, Tabs, Tab, Badge } from "react-bootstrap";
import AuthService from "services/auth.service";
import PrescriptionService from "services/prescription.service";
import Spinner from "react-bootstrap/Spinner";
import "pages/Prescription/Prescription.scss";
import PrescriptionDetails from "./PrescriptionDetails";
import PreviousBtn from "assets/icons/Previous.svg";
import NextBtn from "assets/icons/Next.svg";
import { ReactComponent as HistoryIcon } from "assets/icons/history-icon.svg";

export function Prescription() {
  const [listLoading, setListLoading] = useState(false);
  const [prescriptions, setPrescriptions] = useState([]);
  const [prescription, setPrescription] = useState({});
  const [filter, setFilter] = useState("pending_confirmation");
  const [metadata, setMetadata] = useState({});
  const [activePrescriptionId, setActivePrescriptionId] = useState(null);

  useEffect(() => {
    setPrescription({});
    setActivePrescriptionId(null);
    getPrescriptionsList(1);
  }, [filter]);

  const statusTypes = [
    { type: "pending_confirmation", name: "request" },
    { type: "quote", name: "pending" },
    { type: "prepare", name: "prepare" },
    { type: "ready", name: "ready" },
    { type: "history", name: "history" },
  ];

  const getPrescriptionsList = (current_page) => {
    setListLoading(true);
    const params = {
      status: filter,
      page: current_page,
      per: 10,
    };
    PrescriptionService.getPrescriptions(params).then(
      (data) => {
        setPrescriptions(data.prescriptions);
        setMetadata(data.metadata);

        // Default to the first prescription if no active prescription is set
        setListLoading(false);
        if (data.prescriptions.length > 0) {
          const firstPrescription = data.prescriptions[0];
          setPrescription(firstPrescription);
          setActivePrescriptionId(firstPrescription.id);
        }
      },
      (error) => {
        if (error.response.status === 401) {
          AuthService.logout();
          window.location.replace("/eb/login");
        }
      }
    );
  };

  const switchToTab = (tabType) => {
    setFilter(tabType);
  };

  const showDot = (status_type) => {
    if (
      (status_type === "pending_confirmation" && metadata?.pending_request) ||
      (status_type === "quote" && metadata?.pending_quotation) ||
      (status_type === "prepare" && metadata?.pending_prepare) ||
      (status_type === "ready" && metadata?.pending_ready)
    ) {
      return "d-flex";
    } else {
      return "d-none";
    }
  };

  const DeliveryBadge = ({ courier }) => {
    if (courier !== "Lalamove") {
      return (
        <Badge className="badge">
          <div className="next-day rounded-pill">NEXT DAY</div>
        </Badge>
      );
    } else {
      return (
        <Badge className="badge">
          <div className="express rounded-pill">EXPRESS</div>
        </Badge>
      );
    }
  };

  const StatusName = ({ name, filter }) => {
    if (name === "history") {
      return (
        <HistoryIcon
          style={{ fill: filter === "history" ? "#FF725E" : "#212121" }}
        />
      );
    } else {
      return name.toUpperCase();
    }
  };

  const handlePageChange = (i) => {
    let newPage = metadata.current_page + i;
    if (newPage > 0 && newPage <= metadata.total_pages) {
      getPrescriptionsList(newPage);
    }
  };

  return (
    <div id="erx-main-page" className="erx-page">
      <Container fluid>
        <Row className="wrapper">
          <Col md={3} className="prescriptions-list-container scrollbar-grey">
            <div>
              <h4 className="ml-3 py-2">Medicine Delivery</h4>
              <Tabs
                activeKey={filter}
                className="flex-nowrap status-tabs"
                onSelect={(k) => setFilter(k)}
              >
                {statusTypes.map((status, index) => (
                  <Tab
                    eventKey={status.type}
                    title={
                      <span className="d-flex justify-content-center align-items-center">
                        <span className={`dot ${showDot(status.type)}`}></span>
                        <StatusName
                          name={status.name}
                          filter={filter}
                        ></StatusName>
                      </span>
                    }
                    key={`chat-tab-${index}`}
                  >
                    {(() => {
                      if (listLoading) {
                        return (
                          <div className="loader">
                            <Spinner animation="border" className="spinner" />
                          </div>
                        );
                      } else if (prescriptions.length === 0) {
                        return (
                          <div className="w-100 p-3 text-center mt-3">
                            <p className="empty-chat-subtext">
                              No prescription currently
                            </p>
                          </div>
                        );
                      } else {
                        return (
                          <div className="prescriptions-list scrollbar-grey">
                            {prescriptions.map((p, i) => (
                              <div
                                className={`d-flex prescription-row ${
                                  activePrescriptionId === p.id ? "active" : ""
                                }`}
                                onClick={() => {
                                  setPrescription(p);
                                  setActivePrescriptionId(p.id);
                                }}
                                key={`prescription-row-${i}`}
                              >
                                <div className="d-flex justify-content-center align-items-center">
                                  <div className="details">
                                    <div className="ref-no">
                                      {p.reference_no}
                                    </div>
                                    <div className="item-count">
                                      {p.item_count} item
                                      {p.item_count > 1 && "s"}
                                    </div>
                                  </div>
                                </div>
                                {p?.courier && p?.status !== "quote" && (
                                  <DeliveryBadge
                                    courier={p.courier}
                                  ></DeliveryBadge>
                                )}
                              </div>
                            ))}
                            <div className="pagination justify-content-center align-items-center">
                              <button className="btn btn-link">
                                <img
                                  src={PreviousBtn}
                                  alt="previous"
                                  className="page-prev-btn"
                                  onClick={() => handlePageChange(-1)}
                                />
                              </button>

                              <b className="font-size-4 px-1 count">
                                {metadata.current_page} of{" "}
                                {metadata.total_pages}
                              </b>

                              <button className="btn btn-link">
                                <img
                                  src={NextBtn}
                                  alt="next"
                                  className="page-next-btn"
                                  onClick={() => handlePageChange(1)}
                                />
                              </button>
                            </div>
                          </div>
                        );
                      }
                    })()}
                  </Tab>
                ))}
              </Tabs>
            </div>
          </Col>
          <PrescriptionDetails
            id={prescription?.id}
            switchToTab={switchToTab}
          />
        </Row>
      </Container>
    </div>
  );
}

export default Prescription;
